/**
 * Rich-сообщение: дерево блоков `sendRichMessage`. Решение Ильи 06.09.2026,
 * `docs/phase-2/08-visuals.md` §1.2ж, живые пробы — `docs/prototypes/post-card/probe-results/`.
 *
 * ЗАЧЕМ ЭТОТ ФАЙЛ ВООБЩЕ ЕСТЬ. Подписывается по-прежнему ПЛОСКАЯ каноническая форма
 * `{text, entities}` плюс дескрипторы медиа и документа. Дерево блоков нигде не хранится и не
 * подписывается: оно ВЫВОДИТСЯ отсюда чистой функцией в момент публикации и разбирается
 * обратно при сверке. Поэтому `canonicalize()`, контентный хэш, ключ одобрения и превью в
 * интерфейсе остались в прежней форме, а третий способ отправки — забота последней мили.
 *
 * ТРИ ВЕЩИ, КОТОРЫЕ ЗДЕСЬ НЕЛЬЗЯ ДЕЛАТЬ, И КАЖДАЯ ПРОВЕРЕНА ЖИВЫМ ОТВЕТОМ:
 *
 * 1. СРАВНИВАТЬ ДЕРЕВО С ДЕРЕВОМ. Сервер его нормализует: массив из одного узла
 *    разворачивает в узел (`probe-results/C1-rich-photo-first.json`), ячейкам таблицы
 *    дописывает `align`/`valign` (`W2-structure-no-media.json`), а блок фото возвращает
 *    лесенкой `PhotoSize` вместо ссылки `attach://`. Литеральное сравнение давало бы
 *    расхождение на КАЖДОМ посте. Сверка идёт через `flattenRich` и посимвольную карту
 *    атрибутов (ПРАВИЛО 3.1а).
 * 2. СТРОИТЬ СВОЁ ВЛОЖЕНИЕ. Алгоритм вложения в репозитории один — `buildTree` из
 *    `tree.ts` (§Хребет данных 3.2), и он же приносит защиту от разрыва суррогатной пары.
 *    Второй обход, написанный «почти так же», однажды разойдётся с кодировщиком.
 * 3. РЕЗАТЬ ТЕКСТ НА АБЗАЦЫ-БЛОКИ. До 16.09.2026 текст резался по `\n\n` на отдельные блоки
 *    `paragraph`, а сама пустая строка не отправлялась. Клиенты рисуют между блоками не
 *    пустую строку, а собственный отступ: Android — 3–4,66dp (`RichMessageLayout.java`,
 *    `setContentPadding`/`getGap`) против полной строки текста в обычном сообщении, и пост с
 *    картинкой в канале выглядел заметно плотнее текстового. API отступами не управляет: ни
 *    блока-разделителя «воздуха», ни поля отступа в Bot API нет. Поэтому текст уходит
 *    ОДНИМ блоком, пустые строки — внутри строки текста, и клиент рисует их так же, как в
 *    обычном сообщении (Android StaticLayout, tdesktop, Web K pre-wrap, Web A `<br>`).
 *
 * ЦИТАТЫ И КОД — БЛОКАМИ (19.09.2026, docs/release-2026-09-19.md §9.3). В `RichText` Bot API
 * 10.3 узлов `pre`, `blockquote` и `expandable_blockquote` НЕТ — есть только блоки
 * `InputRichBlockPreformatted {text, language?}`, `InputRichBlockBlockQuotation {blocks}` и
 * `InputRichBlockExpandableBlockQuotation {text}` (сверено с core.telegram.org/bots/api в тот
 * же день). До этой правки такие сущности уходили узлами внутри абзаца, то есть rich-пост с
 * цитатой или кодом получил бы 400 уже после одобрения. Теперь текст режется ровно по ним:
 * абзац — блок цитаты или кода — абзац. Резать можно только по целым строкам, поэтому
 * блочная сущность обязана начинаться с начала строки и кончаться её концом. Разметка
 * исходника даёт это по построению НЕ сама собой, а своими отказами (находка
 * publisher-wire-4): цитата — префикс строки `> `/`>> `, а забор ``` посреди строки, текст за
 * закрывающим забором и забор в цитате без префикса на строках кода — отказ разбора с местом
 * (`telegram/source-markup.ts`, предел 5), и писатель его чинит. До 19.09.2026 такие заборы
 * принимались, и версию отбивала уже заморозка — у писателя мимо починки разметки. Сущности,
 * пришедшие мимо разметки (фикстуры, intake API), и вложение, которого rich не выражает (код в
 * раскрывающейся цитате), отбивает при заморозке `richBlockIssues` (`rich_block_misaligned`), а
 * не Telegram после одобрения.
 * Переводы строк на стыке абзаца и блока не отправляются: блок сам и есть граница, и пустая
 * строка перед цитатой нарисовалась бы вторым зазором. Пост без цитат и кода уходит, как
 * прежде, одним абзацем — ни байта его формы эта правка не меняет.
 *
 * ДОКУМЕНТ (PDF) — ПОСЛЕДНИМ блоком `document` (§9.2), как картинка — первым: это правило
 * вёрстки, а не настройка формата. Имя файла у блока не задаётся — его несёт часть multipart.
 *
 * `flattenRich` ТОТАЛЬНА. Вернувшееся от сервера авторитетно, его нельзя «не принять»: на
 * неизвестный узел она не бросает, а копит его тип в `unknown_node_types`, и это сигнал
 * сверки. Бросок отсюда ушёл бы в публикатор уже ПОСЛЕ записи `sent` в реестр и ДО отчёта
 * бэкенду — пост в канале, локально помечен опубликованным, бэкенд про него не знает.
 *
 * АВТООПРЕДЕЛЕНИЕ (19.09.2026, находка publisher-wire-1). В вернувшемся дереве есть узлы, которых
 * мы не отправляем: сервер сам размечает хэштеги, @упоминания, голые ссылки, почту и телефоны
 * (docs/telegram.md §3) — `skip_entity_detection` мы не передаём, кликабельность требует ПУБ-3.
 * Это не «неизвестное»: они разбираются в сущности автоопределяемых типов, и сверка вычитает их
 * из обеих карт так же, как у обычного сообщения. Неизвестными они считались до этой правки — и
 * каждый пост с хэштегом, опубликованный rich-сообщением, давал critical `publish_deviation`.
 */

import type { CanonicalDraft, MessageEntity } from './draft.ts';
import type { AutoDetectedEntityType } from './entity.ts';
import { buildTree, type PreviewEntity, type PreviewNode } from './tree.ts';

// ---------------------------------------------------------------------------
// Форма дерева
// ---------------------------------------------------------------------------

/**
 * Имена ВСТРОЕННЫХ узлов — ТОЧНО как в Bot API (`RichText…`). `text_link` там называется
 * `url`. Цитат и кода среди них нет: это блоки (шапка файла).
 */
export type RichNodeType =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strikethrough'
  | 'spoiler'
  | 'code'
  | 'url';

export interface RichNode {
  readonly type: RichNodeType;
  readonly text: RichText;
  /** Только у `url`. */
  readonly url?: string;
}

/** Строка, узел или их последовательность: рекурсия и есть вложенность разметки. */
export type RichText = string | RichNode | readonly RichText[];

export type RichBlock =
  | { readonly type: 'paragraph'; readonly text: RichText }
  | { readonly type: 'pre'; readonly text: RichText; readonly language?: string }
  | { readonly type: 'blockquote'; readonly blocks: readonly RichBlock[] }
  | { readonly type: 'expandable_blockquote'; readonly text: RichText }
  | { readonly type: 'photo'; readonly photo: { readonly type: 'photo'; readonly media: string } }
  | {
      readonly type: 'document';
      readonly document: { readonly type: 'document'; readonly media: string };
    };

export interface RichMessage {
  readonly blocks: readonly RichBlock[];
}

/**
 * Отображение ВСТРОЕННЫХ сущностей в узлы rich. Полное по отправляемым типам за вычетом
 * блочных: неполное молча теряло бы разметку целого класса черновиков. `text_mention` и
 * `custom_emoji` в отправляемых не значатся (`entity.ts`), поэтому их здесь и нет.
 */
const NODE_TYPE: Readonly<Record<string, RichNodeType>> = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  strikethrough: 'strikethrough',
  spoiler: 'spoiler',
  code: 'code',
  text_link: 'url',
};

/**
 * Обратное отображение для разбора ОТВЕТА. `Map`, а не объект: тип узла приходит от сервера, и
 * имя вроде `constructor` в обычном объекте нашлось бы в прототипе — разбор перестал бы быть
 * тотальным на первом же странном узле.
 */
const ENTITY_TYPE: ReadonlyMap<string, string> = new Map(
  Object.entries(NODE_TYPE).map(([entity, node]) => [node, entity]),
);

/**
 * Узлы, которые сервер ставит САМ, — автоопределение (docs/telegram.md §3). Bot API 10.3,
 * «Rich Message Formatting Options»: голые ссылки, почта, @упоминания, #хэштеги, $кэштеги,
 * /команды, телефоны и номера карт находятся автоматически, пока в запросе нет
 * `skip_entity_detection`, — а мы его не передаём: кликабельные хэштеги и ссылки — требование
 * продукта (ПУБ-3, ПРАВИЛО 3.2). Найденное приходит узлами `RichText` внутри абзаца —
 * `RichTextMention {text, username}`, `RichTextHashtag {text, hashtag}`, … (сверено с
 * core.telegram.org/bots/api 19.09.2026). Текст у всех — в поле `text`, как у наших узлов.
 *
 * Имена сущностей — ровно `AUTO_DETECTED_ENTITY_TYPES` (`entity.ts`), под которыми те же находки
 * приходят в `Message.entities` обычного сообщения: сверка вычитает их из обеих карт, и набор у
 * обоих транспортов обязан быть один. `email_address` узла — `email` сущности.
 *
 * `null` — узел известен, но атрибута не даёт: номер карты. В `Message.entities` сервер его
 * вырезает (ПРАВИЛО 6.2, `STRIPPED_BY_SERVER_TYPES`), а в дереве отдаёт узлом; отправить его
 * нельзя, поэтому в плоскую форму идёт только его текст.
 *
 * Узла `url` здесь нет намеренно. Им сервер возвращает и НАШУ ссылку (`text_link`), и ссылку,
 * найденную в тексте сам (голый адрес, домен без схемы). По дереву их не различить — различает
 * подписанное, и делает это сверка (`verify.ts`, шаг 4а). Здесь `url` — всегда `text_link`.
 */
const AUTO_NODE_ENTITY_TYPE: ReadonlyMap<string, AutoDetectedEntityType | null> = new Map([
  ['mention', 'mention'],
  ['hashtag', 'hashtag'],
  ['cashtag', 'cashtag'],
  ['bot_command', 'bot_command'],
  ['email_address', 'email'],
  ['phone_number', 'phone_number'],
  ['bank_card_number', null],
]);

/** Сущности, которые в rich-сообщении становятся БЛОКАМИ. Имена блоков совпадают с ними. */
export const RICH_BLOCK_ENTITY_TYPES = ['pre', 'blockquote', 'expandable_blockquote'] as const;
export type RichBlockEntityType = (typeof RICH_BLOCK_ENTITY_TYPES)[number];
const BLOCK_SET: ReadonlySet<string> = new Set(RICH_BLOCK_ENTITY_TYPES);

export function isRichBlockEntityType(t: string): t is RichBlockEntityType {
  return BLOCK_SET.has(t);
}

export class RichUnrepresentableError extends Error {
  override readonly name = 'RichUnrepresentableError';
}

/**
 * Как `flattenRich` склеивает соседние текстовые блоки. Отправляем мы один абзац (пункт 3
 * шапки) или абзацы вперемежку с цитатами и кодом, но разбор тотален: блоков в ответе может
 * оказаться больше, и склейка одинакова для отправленного и вернувшегося.
 */
const PARAGRAPH_SEPARATOR = '\n\n';

// ---------------------------------------------------------------------------
// Проверка: блочные сущности занимают целые строки
// ---------------------------------------------------------------------------

export type RichBlockProblemKind = 'start' | 'end' | 'nested' | 'overlap';

export interface RichBlockProblem {
  readonly kind: RichBlockProblemKind;
  readonly type: RichBlockEntityType;
  readonly offset: number;
  readonly length: number;
  /** По-русски, для человека на экране одобрения и в ответе 422. */
  readonly message: string;
}

const BLOCK_WORD: Readonly<Record<RichBlockEntityType, string>> = {
  pre: 'Блок кода',
  blockquote: 'Цитата',
  expandable_blockquote: 'Раскрывающаяся цитата',
};

interface Span {
  readonly type: RichBlockEntityType;
  readonly from: number;
  readonly to: number;
  readonly language?: string | undefined;
}

function blockSpans(entities: readonly MessageEntity[]): Span[] {
  const out: Span[] = [];
  for (const e of entities) {
    if (!isRichBlockEntityType(e.type)) continue;
    out.push({
      type: e.type,
      from: e.offset,
      to: e.offset + e.length,
      ...(e.type === 'pre' && e.language !== undefined ? { language: e.language } : {}),
    });
  }
  // Родитель раньше ребёнка: по началу, затем длинный раньше короткого; при равном отрезке
  // цитата раньше кода — код внутри цитаты законен, цитата внутри кода — нет.
  out.sort(
    (a, b) =>
      a.from - b.from || b.to - a.to || (a.type === 'pre' ? 1 : 0) - (b.type === 'pre' ? 1 : 0),
  );
  return out;
}

/**
 * ПРЕДУСЛОВИЕ `richFromCanonical`: каждая блочная сущность — целые строки, вложение только
 * «код внутри обычной цитаты», пересечений нет. Зовут её заморозка (`checkLimits`, код
 * `rich_block_misaligned`) и сама сборка дерева. Пустой ответ — дерево строится.
 */
export function richBlockIssues(
  text: string,
  entities: readonly MessageEntity[],
): RichBlockProblem[] {
  const spans = blockSpans(entities);
  const out: RichBlockProblem[] = [];
  const problem = (kind: RichBlockProblemKind, s: Span, message: string): void => {
    out.push({ kind, type: s.type, offset: s.from, length: s.to - s.from, message });
  };

  for (const s of spans) {
    const word = BLOCK_WORD[s.type];
    if (s.from > 0 && text.charCodeAt(s.from - 1) !== 0x0a) {
      problem(
        'start',
        s,
        `${word} начинается посреди строки: в расширенном сообщении она занимает целые строки.`,
      );
    }
    if (s.to < text.length && text.charCodeAt(s.to) !== 0x0a) {
      problem(
        'end',
        s,
        `${word} кончается посреди строки: в расширенном сообщении она занимает целые строки.`,
      );
    }
  }

  for (let i = 0; i < spans.length; i++) {
    const inner = spans[i] as Span;
    for (let j = 0; j < i; j++) {
      const outer = spans[j] as Span;
      if (outer.to <= inner.from) continue;
      const contains = outer.from <= inner.from && inner.to <= outer.to;
      if (!contains) {
        problem('overlap', inner, `${BLOCK_WORD[inner.type]} пересекается с другим блоком.`);
      } else if (!(outer.type === 'blockquote' && inner.type === 'pre')) {
        problem(
          'nested',
          inner,
          `${BLOCK_WORD[inner.type]} внутри блока «${BLOCK_WORD[outer.type].toLowerCase()}» ` +
            'в расширенном сообщении не выражается: внутри цитаты бывает только блок кода.',
        );
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Плоское → дерево
// ---------------------------------------------------------------------------

function nodeOf(entity: PreviewEntity, text: RichText): RichNode {
  const type = NODE_TYPE[entity.type];
  if (type === undefined) {
    // Бросок ловится сборкой запроса (шаг 28 публикатора) и превращается в отказ
    // публикации с именем типа. Молча пропустить сущность нельзя: пост уехал бы без
    // разметки, а сверка потом объявила бы это расхождением нашей же вины.
    throw new RichUnrepresentableError(`сущность ${entity.type} не выражается узлом rich`);
  }
  if (type === 'url') {
    if (entity.url === undefined) {
      throw new RichUnrepresentableError('text_link без url');
    }
    return { type, text, url: entity.url };
  }
  return { type, text };
}

function toRichText(nodes: readonly PreviewNode[]): RichText {
  const out: RichText[] = nodes.map((n) =>
    n.kind === 'text' ? n.text : nodeOf(n.entity, toRichText(n.children)),
  );
  // Массив из одного элемента сервер всё равно развернёт (проба C1) — разворачиваем сами,
  // чтобы отправленное и вернувшееся отличались на одну нормализацию меньше.
  return out.length === 1 ? (out[0] as RichText) : out;
}

function previewEntityOf(e: MessageEntity): PreviewEntity {
  return {
    type: e.type,
    offset: e.offset,
    length: e.length,
    ...('url' in e && e.url !== undefined ? { url: e.url } : {}),
    ...('language' in e && e.language !== undefined ? { language: e.language } : {}),
  };
}

/**
 * Встроенная разметка отрезка `[from, to)`: сущности обрезаются по отрезку и сдвигаются в
 * его начало. Обрезка вложенных и непересекающихся отрезков по одной границе вложения не
 * ломает, а границы — концы строк и концы сущностей — суррогатную пару не режут никогда;
 * `buildTree` всё равно это проверит.
 */
function inlineOf(
  text: string,
  inline: readonly MessageEntity[],
  from: number,
  to: number,
): RichText {
  const clipped: PreviewEntity[] = [];
  for (const e of inline) {
    const s = Math.max(e.offset, from);
    const t = Math.min(e.offset + e.length, to);
    if (t > s) clipped.push({ ...previewEntityOf(e), offset: s - from, length: t - s });
  }
  return toRichText(buildTree(text.slice(from, to), clipped));
}

/** Отрезок абзаца между блоками: переводы строк на краях — это стык с блоком, не текст. */
function paragraphBlock(
  text: string,
  inline: readonly MessageEntity[],
  from: number,
  to: number,
): RichBlock | null {
  let a = from;
  let b = to;
  while (a < b && text.charCodeAt(a) === 0x0a) a++;
  while (b > a && text.charCodeAt(b - 1) === 0x0a) b--;
  if (text.slice(a, b).trim() === '') return null;
  return { type: 'paragraph', text: inlineOf(text, inline, a, b) };
}

/** Блоки отрезка: абзацы между блочными сущностями верхнего уровня и сами блоки. */
function blocksOf(
  text: string,
  inline: readonly MessageEntity[],
  spans: readonly Span[],
  from: number,
  to: number,
): RichBlock[] {
  // Сам родитель сюда не приходит: вызов для цитаты передаёт только вложенные в неё блоки.
  // Отрезок, равный родительскому, — законный ребёнок (цитата, целиком занятая кодом).
  const top: Span[] = [];
  for (const s of spans) {
    if (s.from < from || s.to > to) continue;
    if (top.some((t) => t.from <= s.from && s.to <= t.to)) continue; // внук
    top.push(s);
  }

  const out: RichBlock[] = [];
  let cursor = from;
  for (const s of top) {
    const before = paragraphBlock(text, inline, cursor, s.from);
    if (before !== null) out.push(before);
    if (s.type === 'pre') {
      out.push({
        type: 'pre',
        text: inlineOf(text, inline, s.from, s.to),
        ...(s.language !== undefined ? { language: s.language } : {}),
      });
    } else if (s.type === 'expandable_blockquote') {
      out.push({ type: 'expandable_blockquote', text: inlineOf(text, inline, s.from, s.to) });
    } else {
      const inner = spans.filter((x) => x !== s && x.from >= s.from && x.to <= s.to);
      out.push({
        type: 'blockquote',
        blocks:
          inner.length === 0
            ? [{ type: 'paragraph', text: inlineOf(text, inline, s.from, s.to) }]
            : blocksOf(text, inline, inner, s.from, s.to),
      });
    }
    cursor = s.to;
  }
  const after = paragraphBlock(text, inline, cursor, to);
  if (after !== null) out.push(after);
  return out;
}

/**
 * ЕДИНСТВЕННЫЙ источник дерева блоков. Чистая, детерминированная.
 *
 * `mediaAttach` — `attach://<имя части multipart>` либо `file_id`; `null` — картинки нет.
 * Блок фото ВСЕГДА первый: «картинка сверху, перед любым текстом» — решение Ильи, а не
 * настройка формата. `documentAttach` — так же для PDF; блок документа ВСЕГДА последний.
 *
 * Текст без цитат и кода — ОДНИМ блоком `paragraph` целиком, вместе с пустыми строками и
 * сущностями без обрезки (пункт 3 шапки). С цитатами и кодом — абзацы между ними и сами
 * блоки; предусловие — `richBlockIssues` пуст, иначе `RichUnrepresentableError`.
 */
export function richFromCanonical(
  canonical: Pick<CanonicalDraft, 'text' | 'entities'>,
  mediaAttach: string | null,
  documentAttach: string | null = null,
): RichMessage {
  const blocks: RichBlock[] = [];
  if (mediaAttach !== null) {
    blocks.push({ type: 'photo', photo: { type: 'photo', media: mediaAttach } });
  }

  const { text, entities } = canonical;
  const spans = blockSpans(entities);
  if (spans.length === 0) {
    blocks.push({
      type: 'paragraph',
      text: toRichText(buildTree(text, entities.map(previewEntityOf))),
    });
  } else {
    const problems = richBlockIssues(text, entities);
    if (problems.length > 0) {
      throw new RichUnrepresentableError((problems[0] as RichBlockProblem).message);
    }
    const inline = entities.filter((e) => !isRichBlockEntityType(e.type));
    blocks.push(...blocksOf(text, inline, spans, 0, text.length));
  }

  if (documentAttach !== null) {
    blocks.push({ type: 'document', document: { type: 'document', media: documentAttach } });
  }
  return { blocks };
}

// ---------------------------------------------------------------------------
// Дерево → плоское
// ---------------------------------------------------------------------------

/**
 * Сущность плоской формы: наша (отправляемая) либо найденная сервером сама. Вторые бывают только
 * в плоской форме ОТВЕТА: дерево, которое строит `richFromCanonical`, их не содержит.
 */
export type FlatEntity =
  | MessageEntity
  | { readonly type: AutoDetectedEntityType; readonly offset: number; readonly length: number };

export interface FlattenedRich {
  readonly text: string;
  readonly entities: FlatEntity[];
  /** Типы узлов и блоков, которых мы не отправляем. Сигнал сверки, а не повод для броска. */
  readonly unknown_node_types: string[];
}

interface FlattenState {
  out: string;
  entities: FlatEntity[];
  unknown: Set<string>;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function walkRichText(node: unknown, st: FlattenState): void {
  if (typeof node === 'string') {
    st.out += node;
    return;
  }
  if (Array.isArray(node)) {
    for (const n of node) walkRichText(n, st);
    return;
  }
  if (!isRecord(node)) return;

  const start = st.out.length;
  walkRichText(node['text'], st);
  const length = st.out.length - start;

  const nodeType = typeof node['type'] === 'string' ? node['type'] : '';
  const auto = AUTO_NODE_ENTITY_TYPE.get(nodeType);
  if (auto !== undefined) {
    // Находка сервера (шапка, «Автоопределение»): текст уже в плоской форме, атрибут — тип
    // автоопределения; у номера карты атрибута нет.
    if (auto !== null && length > 0) st.entities.push({ type: auto, offset: start, length });
    return;
  }
  const entityType = ENTITY_TYPE.get(nodeType);
  if (entityType === undefined) {
    if (nodeType !== '') st.unknown.add(nodeType);
    return;
  }
  if (length === 0) return;

  const e: Record<string, unknown> = { type: entityType, offset: start, length };
  if (entityType === 'text_link' && typeof node['url'] === 'string') e['url'] = node['url'];
  st.entities.push(e as unknown as MessageEntity);
}

/** Блоки без текста: в плоскую форму не входят, их место проверяется отдельно. */
const MEDIA_BLOCKS: ReadonlySet<string> = new Set(['photo', 'document']);

function walkBlocks(blocks: readonly unknown[], st: FlattenState): void {
  let first = true;
  for (const b of blocks) {
    if (!isRecord(b)) continue;
    const type = b['type'];
    if (typeof type === 'string' && MEDIA_BLOCKS.has(type)) continue;
    if (type !== 'paragraph' && !(typeof type === 'string' && isRichBlockEntityType(type))) {
      if (typeof type === 'string') st.unknown.add(type);
      continue;
    }
    if (!first) st.out += PARAGRAPH_SEPARATOR;
    first = false;

    const start = st.out.length;
    if (type === 'blockquote') {
      const inner = b['blocks'];
      walkBlocks(Array.isArray(inner) ? inner : [], st);
    } else {
      walkRichText(b['text'], st);
    }
    const length = st.out.length - start;
    if (type === 'paragraph' || length === 0) continue;

    const e: Record<string, unknown> = { type, offset: start, length };
    if (type === 'pre' && typeof b['language'] === 'string') e['language'] = b['language'];
    st.entities.push(e as unknown as MessageEntity);
  }
}

/**
 * Обратный ход. Принимает И отправленную форму, И вернувшуюся — это РАЗНЫЕ формы, см. шапку.
 * Тотальна: ничего не бросает, неизвестное копит.
 */
export function flattenRich(blocks: unknown): FlattenedRich {
  const st: FlattenState = { out: '', entities: [], unknown: new Set() };
  if (!Array.isArray(blocks)) {
    return { text: '', entities: [], unknown_node_types: [] };
  }
  walkBlocks(blocks, st);
  st.entities.sort((a, b) => a.offset - b.offset || a.length - b.length);
  return { text: st.out, entities: st.entities, unknown_node_types: [...st.unknown].sort() };
}

/** Первый блок — фото, и в нём непустая лесенка размеров. Проверка вёрстки, а не наличия. */
export function richPhotoIsFirst(blocks: unknown): boolean {
  if (!Array.isArray(blocks) || blocks.length === 0) return false;
  const first: unknown = blocks[0];
  if (!isRecord(first) || first['type'] !== 'photo') return false;
  const photo = first['photo'];
  return Array.isArray(photo) ? photo.length > 0 : isRecord(photo);
}

/**
 * Где в ответе лежит документ: `last` — последним блоком (так, как отправляли), `elsewhere`
 * — есть, но не последним, `missing` — нет вовсе. Имя файла — как его вернул сервер
 * (`Document.file_name`), либо `null`, если поля нет.
 */
export function richDocumentPlacement(blocks: unknown): {
  at: 'last' | 'elsewhere' | 'missing';
  index: number;
  fileName: string | null;
} {
  if (!Array.isArray(blocks)) return { at: 'missing', index: -1, fileName: null };
  const index = blocks.findIndex((b) => isRecord(b) && b['type'] === 'document');
  if (index < 0) return { at: 'missing', index, fileName: null };
  const block = blocks[index] as Record<string, unknown>;
  const doc = block['document'];
  const name = isRecord(doc) && typeof doc['file_name'] === 'string' ? doc['file_name'] : null;
  return { at: index === blocks.length - 1 ? 'last' : 'elsewhere', index, fileName: name };
}
